/*
Plate status derivation for the Emails tab campaign list.

plateStatus determines whether a campaign has a template for a given language:
- 'present': the template exists
- 'missing': the template should exist but doesn't (a real gap)
- 'not-needed': the campaign is list-kind, which falls through to the generic
  newsletter by design, so a missing plate is not a defect.

buildPlates collects all plates for a campaign across a set of languages.
campaignMatchesFilter answers whether a campaign should be shown given the
current filter state.
compareCampaignsByNewest orders the list, newest first.

CommonJS on purpose: no bundler, runs under plain node src/utils/emailPlates.test.js
*/

const plateStatus = (campaign, language) => {
  // List-kind campaigns have no bespoke email by design
  if (campaign.kind === 'list') {
    return 'not-needed';
  }

  // For campaign- and system-kind, look up the language
  if (campaign.languages) {
    const plate = campaign.languages.find(p => p.language === language);
    if (plate) {
      return plate.status;
    }
  }

  // Default to missing when not found
  return 'missing';
};

const buildPlates = (campaign, languages) => languages.map(language => ({
  language,
  status: plateStatus(campaign, language),
}));

/*
Newest campaign first.

Sorts on `createdAt`, falling back to the key when two campaigns share a
timestamp - which is not a rare edge here. 56 of staging's 58 campaigns were
written by one seed run inside three seconds, so the dates separate the ones
someone actually made from the seeded bulk and little else. The key tiebreak
keeps the rest in a stable, explicable order rather than leaving it to whatever
order Mongo happened to return.

A campaign with no `createdAt` sorts LAST, not first. Missing is not newer, and
a record written before the model carried timestamps is by definition among the
oldest things here. An unparseable date is treated the same way as a missing
one, so a bad value can never reorder the list around itself.
*/
const compareCampaignsByNewest = (a, b) => {
  const timeOf = (campaign) => {
    const parsed = campaign.createdAt ? Date.parse(campaign.createdAt) : NaN;
    return Number.isNaN(parsed) ? null : parsed;
  };

  const byKey = () => String(a.key || '').localeCompare(String(b.key || ''));

  const left = timeOf(a);
  const right = timeOf(b);

  if (left === null && right === null) {
    return byKey();
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  if (left !== right) {
    return right - left;
  }
  return byKey();
};

const campaignMatchesFilter = (campaign, filter) => {
  // Default empty filter matches everything
  if (!filter) {
    return true;
  }

  // Check kind filter (if provided and non-empty, must match)
  if (filter.kind && filter.kind !== '') {
    if (campaign.kind !== filter.kind) {
      return false;
    }
  }

  // Check activeOnly filter
  if (filter.activeOnly && !campaign.isActive) {
    return false;
  }

  // Check search filter (case-insensitive search in key and title)
  if (filter.search) {
    const searchLower = filter.search.toLowerCase();
    const keyMatches = campaign.key && campaign.key.toLowerCase().includes(searchLower);
    const titleMatches = campaign.title && campaign.title.toLowerCase().includes(searchLower);
    if (!keyMatches && !titleMatches) {
      return false;
    }
  }

  return true;
};

/*
The editor's plate switcher (design screen 3) works off the templates that
actually exist for a scope, not off the campaign row's derived statuses, so it
can tell the two "has a plate" cases apart:

- 'is-set'    the plate is set and has been looked at
- 'is-stale'  the plate exists but is flagged needsReview - it was copied by a
              clone and nobody has opened it since. This is the state the whole
              clone flow exists to make visible: last year's presale copy, word
              for word, under this year's campaign.
- 'is-empty'  no plate for that language at all

The design's other two states, 'is-fallback' and 'is-broken', are not derivable
from anything the client can see today - see task-10-report.md.
*/
const plateCellState = (template) => {
  if (!template) {
    return 'is-empty';
  }
  return template.needsReview ? 'is-stale' : 'is-set';
};

const buildScopePlates = (templates, languages, currentLanguage) => languages.map((language) => {
  const template = (templates || []).find(item => item && item.language === language);
  return {
    language,
    state: plateCellState(template),
    exists: Boolean(template),
    needsReview: Boolean(template && template.needsReview),
    subject: (template && template.subject) || '',
    isCurrent: language === currentLanguage,
  };
});

/*
A plate strip can be in three different truths, and collapsing them loses the
only one that matters:

  'ready'    we asked and we know
  'loading'  we have not asked yet
  'failed'   we asked and the answer never came

Before buildPlateStrip existed, the last two both arrived as an empty template
list, which buildScopePlates turned into 'is-empty' cells - a positive claim
that those plates do NOT exist, indistinguishable from a campaign that really
has none. A transient network failure could therefore tell an editor "there is
no Spanish plate here" while a needsReview Spanish plate sat behind it, ready to
send last year's copy. Under-reporting silently is worse than reporting nothing:
hence the fourth cell state, 'is-unknown', which the UI must render as a
placeholder or a failure, never as an empty square.

The plate being edited is exempt from all of this. It is on screen, so it
exists; it is derived from `currentTemplate` (the template already in the
editor's state) rather than from the fetched list, so neither a pending nor a
failed strip fetch can make the editor claim the plate it is editing is absent.
*/
const PLATE_STRIP_LOADING = 'loading';
const PLATE_STRIP_READY = 'ready';
const PLATE_STRIP_FAILED = 'failed';

const UNKNOWN_PLATE_STATE = 'is-unknown';

const unknownPlate = (language, currentLanguage) => ({
  language,
  state: UNKNOWN_PLATE_STATE,
  exists: false,
  known: false,
  needsReview: false,
  subject: '',
  isCurrent: language === currentLanguage,
});

const buildPlateStrip = ({
  status, templates, languages, currentLanguage, currentTemplate,
} = {}) => {
  const stripStatus = status || PLATE_STRIP_LOADING;
  const ready = stripStatus === PLATE_STRIP_READY;
  const languageList = languages || [];

  const base = ready
    ? buildScopePlates(templates, languageList, currentLanguage)
      .map(plate => ({ ...plate, known: true }))
    : languageList.map(language => unknownPlate(language, currentLanguage));

  const plates = base.map((plate) => {
    if (!plate.isCurrent || !currentTemplate) {
      return plate;
    }
    return {
      ...plate,
      state: plateCellState(currentTemplate),
      exists: true,
      known: true,
      needsReview: Boolean(currentTemplate.needsReview),
      subject: currentTemplate.subject || plate.subject || '',
    };
  });

  return { status: stripStatus, plates };
};

// The languages the "still need review" banner names: every plate flagged
// needsReview except the one being edited, which the editor reports on its own.
//
// Returns null - NOT an empty list - for a strip that is not ready. The two are
// different answers: [] means "checked, nothing needs review" and null means
// "the check could not run". A caller that renders [] as silence would tell an
// editor everything is fine at exactly the moment nothing is known.
const platesNeedingReview = (strip) => {
  if (!strip || strip.status !== PLATE_STRIP_READY) {
    return null;
  }
  return (strip.plates || [])
    .filter(plate => plate.needsReview && !plate.isCurrent)
    .map(plate => plate.language);
};

const KIND_LABELS = {
  campaign: 'Campaign',
  list: 'List',
  system: 'System',
};

module.exports = {
  plateStatus,
  buildPlates,
  plateCellState,
  buildScopePlates,
  buildPlateStrip,
  platesNeedingReview,
  PLATE_STRIP_LOADING,
  PLATE_STRIP_READY,
  PLATE_STRIP_FAILED,
  UNKNOWN_PLATE_STATE,
  campaignMatchesFilter,
  compareCampaignsByNewest,
  KIND_LABELS,
};
